// MACHINERY V2 (Stroje) — fleet dialog state (M2.2).
//
// Six dialogs, one owner. Split out of FleetV1Page so the page stays a
// composition (and under the 300-line cap) and so every open/close pair lives
// next to its sibling — the same dialog is opened from a card kebab AND from
// the toolbar, and split ownership is how a dialog ends up stuck open.
//
// The dialogs themselves are the EXISTING (MUI) ones, mounted by FleetDialogs
// per ticket §5 — this hook only tracks which is open and for which machine.
//
// `newMachineId` lives here because it is set by MachineDialog's onCreated
// callback; the page reads it for the NEW badge.
//
// THE TWO REFRESHES BELOW GO THROUGH `fleetFetchQuery` (MDEL-FE review finding
// 5). They used to pass the raw slice `query`, which also carries the CLIENT-
// side facets FleetShell.tsx:100/105 write into it; sending those narrows the
// response and replaces the complete-set cache with one facet's worth of
// machines, and the load's effect does not watch facets so it never
// self-repairs. Same defect the duplicate / restore / delete refreshes carried.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback, useState } from "react";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { CatalogModelListItem, Machine, MachinesQuery } from "../../models/models";
import {
  getDecommissionedMachines,
  getMachines,
  selectMachine,
} from "../../customerMachinerySlice";
import { fetchGroups } from "../../machineGroupsSlice";
import { FleetDialogsProps } from "./FleetDialogs";
import { fleetFetchQuery } from "./fleetFetchQuery";

export interface FleetDialogControls {
  /** Everything FleetDialogs needs except the slice-selected machine. */
  dialogProps: Omit<FleetDialogsProps, "selectedMachine">;
  newMachineId: string | null;
  setNewMachineId: (id: string | null) => void;
  /**
   * M2.7: the fleet toolbar's "+" no longer calls this — it opens the v2
   * add/AddMachinePicker. This is what that picker calls when the user chooses
   * a CATALOG model; since M2.8 it opens the v2 `CatalogAddDialogV2` rather
   * than v1's MachineDialog. Still the single owner of `addOpen` so the
   * open/close pair stays in one place.
   */
  openCatalogAdd: (model: CatalogModelListItem) => void;
  openManageGroups: () => void;
  openQr: (m: Machine) => void;
  openEdit: (m: Machine) => void;
  openAssignGroup: (m: Machine) => void;
  openDecommission: (m: Machine) => void;
}

/**
 * `onReopenPicker` is REQUIRED, not optional (M2.8). The catalog add form's
 * "Späť na možnosti" / "Zmeniť stroj" controls are drawn in artboard 0903 and
 * `CatalogAddDialogV2` renders them only when the caller passes `onBack` — "the
 * fleet add flow does (the picker), the catalog pages do not". This mount is
 * the flow that HAS a picker, and it was passing nothing, so both controls
 * rendered as empty space: a user who picked the wrong model had to cancel out
 * and reopen the picker from the toolbar. The picker's open flag lives in
 * FleetPage (it is opened from the toolbar, not from here), so re-opening it is
 * the page's to do and this hook takes the callback. Typed required so a future
 * mount cannot re-introduce the dead affordance by omission.
 */
export const useFleetDialogs = (
  query: MachinesQuery,
  onReopenPicker: () => void
): FleetDialogControls => {
  const dispatch = useAppDispatch();
  const [newMachineId, setNewMachineId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [catalogModel, setCatalogModel] = useState<CatalogModelListItem | undefined>(undefined);
  const [qrOpen, setQrOpen] = useState(false);
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);
  const [assignGroupMachine, setAssignGroupMachine] = useState<Machine | null>(null);
  const [editMachine, setEditMachine] = useState<Machine | null>(null);
  const [decommissionMachine, setDecommissionMachine] = useState<Machine | null>(null);

  const openCatalogAdd = useCallback(
    (model: CatalogModelListItem) => {
      // `CatalogAddDialogV2` takes the model as a PROP and never reads the
      // slice, so this no longer feeds the dialog. It is kept because it also
      // clears whatever machine a previous kebab selected: `selectedMachine`
      // still gates the QR dialog in FleetDialogs, and leaving a stale
      // selection behind while an add dialog is open is exactly the
      // wrong-machine hazard `useMachineScope` was written for.
      dispatch(selectMachine(null));
      setCatalogModel(model);
      setAddOpen(true);
    },
    [dispatch]
  );

  const openQr = useCallback(
    (m: Machine) => {
      dispatch(selectMachine(m));
      setQrOpen(true);
    },
    [dispatch]
  );

  const dialogProps: Omit<FleetDialogsProps, "selectedMachine"> = {
    addOpen,
    preSelectedCatalogModel: catalogModel,
    onAddClose: () => {
      setAddOpen(false);
      setCatalogModel(undefined);
    },
    // The back path: close THIS dialog, drop the picked model, and put the
    // picker back on screen. `CatalogAddDialogV2` runs its own `resetAll()`
    // before calling this (its `leave` helper), so no half-typed serial from
    // model A can survive into model B.
    onAddBack: () => {
      setAddOpen(false);
      setCatalogModel(undefined);
      onReopenPicker();
    },
    onCreated: (id?: string) => {
      if (id) setNewMachineId(id);
      // M2.8: CLOSING AND REFRESHING MOVED HERE FROM THE DIALOG.
      // v1's MachineDialog closed itself (MachineDialog.tsx:370) and fired its
      // own `getMachines` (:374). `CatalogAddDialogV2` does neither by design:
      // §9 D7 makes `onCreated` the ONLY close path, reached solely on success,
      // and every v2 mutation refresh goes through `fleetFetchQuery` — same
      // shape as `onAssigned` below. Without this the fleet would keep showing
      // the pre-add list until the next navigation.
      setAddOpen(false);
      setCatalogModel(undefined);
      dispatch(getMachines(fleetFetchQuery(query)));
    },
    qrOpen,
    onQrClose: () => setQrOpen(false),
    manageGroupsOpen,
    onManageGroupsClose: () => setManageGroupsOpen(false),
    assignGroupOpen: !!assignGroupMachine,
    assignGroupMachine,
    onAssignGroupClose: () => setAssignGroupMachine(null),
    onAssigned: () => {
      setAssignGroupMachine(null);
      dispatch(getMachines(fleetFetchQuery(query)));
      // REVIEW F7 — the live handler (Machinery.tsx:779-782) also refreshes the
      // GROUPS. Without it ManageGroups keeps showing pre-assignment member
      // counts until the page is reloaded.
      dispatch(fetchGroups());
    },
    editMachine,
    onEditClose: () => setEditMachine(null),
    onEditSaved: () => setEditMachine(null),
    decommissionMachine,
    onDecommissionClose: () => setDecommissionMachine(null),
    onDecommissioned: () => {
      setDecommissionMachine(null);
      dispatch(getMachines(fleetFetchQuery(query)));
      dispatch(getDecommissionedMachines());
    },
  };

  return {
    dialogProps,
    newMachineId,
    setNewMachineId,
    openCatalogAdd,
    openManageGroups: () => setManageGroupsOpen(true),
    openQr,
    openEdit: setEditMachine,
    openAssignGroup: setAssignGroupMachine,
    openDecommission: setDecommissionMachine,
  };
};

export default useFleetDialogs;
